import { MapPin, Phone } from 'lucide-react';

/**
 * Where the worker has to go.
 *
 * This app answered "when is my appointment" and never "where" — the API
 * did not send a venue and no screen asked for one. For somebody who takes
 * unpaid leave to attend, the address, the landmark and a number to ring are
 * the appointment; the time on its own is half an instruction.
 *
 * Built for a phone at a factory gate on mobile data: text, a `tel:` link and
 * a maps link, no embedded map, no script, nothing that needs a network to be
 * readable once the page has loaded.
 */

export interface Venue {
  id: string;
  name: string;
  addressLine1?: string | null;
  addressLine2?: string | null;
  locality?: string | null;
  landmark?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  emergencyPhone?: string | null;
  operatingDays?: string | null;
  operatingHours?: string | null;
  formattedAddress?: string | null;
  mapsUrl?: string | null;
}

export function VenueCard({ venue, className = '' }: { venue: Venue | null | undefined; className?: string }) {
  if (!venue) return null;

  const cityLine = [venue.city, venue.state].filter(Boolean).join(', ');
  const lines = [
    venue.addressLine1,
    venue.addressLine2,
    venue.locality,
    [cityLine, venue.pincode].filter(Boolean).join(' '),
  ].map((l) => (l ?? '').trim()).filter(Boolean);

  const phone = venue.contactPhone ?? venue.emergencyPhone ?? null;

  return (
    <div className={`mt-3 rounded-lg bg-neutral-50 p-3 ${className}`}>
      <p className="text-sm font-semibold text-neutral-900">{venue.name}</p>

      {lines.length > 0 && (
        <address className="mt-1 not-italic text-sm leading-relaxed text-neutral-700">
          {lines.map((l) => <span key={l} className="block">{l}</span>)}
        </address>
      )}

      {/* The landmark gets its own line and its own emphasis: it is what a
          worker actually navigates by, and it is the part an address written
          for a courier buries. */}
      {venue.landmark && (
        <p className="mt-1 text-sm font-medium text-neutral-800">
          Landmark: {venue.landmark}
        </p>
      )}

      {venue.operatingHours && (
        <p className="mt-1 text-xs text-neutral-600">
          Open {venue.operatingDays ? `${venue.operatingDays}, ` : ''}{venue.operatingHours}
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {venue.mapsUrl && (
          <a
            href={venue.mapsUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-h-11 items-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-4 text-sm font-medium text-neutral-800 active:bg-neutral-100"
          >
            <MapPin aria-hidden className="h-4 w-4" />
            Directions
          </a>
        )}
        {phone && (
          <a
            href={`tel:${phone}`}
            className="inline-flex min-h-11 items-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-4 text-sm font-medium text-neutral-800 active:bg-neutral-100"
          >
            <Phone aria-hidden className="h-4 w-4" />
            {/* The number, not "Call the venue": it can be read out or dialled
                from another phone, and "venue" is the system's word. */}
            Call <span className="tabular">{phone}</span>
          </a>
        )}
      </div>
    </div>
  );
}
